<template>
    <div v-if="userStats.stocks">
        <h1 class="mx-3 mt-3">Stats</h1>

        <rounded-container variant="dark">
            <template v-slot:title>Position dynamic</template>

            <buys-closed-chart
                v-if="gotStatsData"
                :chart-data="barChartData"
                :styles="{ position: 'relative', padding: '10px' }"
                />
            <div v-else style="padding: 10px">
                <chart-skeleton aspect-ratio="2 / 1" />
            </div>
            
            <b-container class="my-3 text-center">
                Average position lifetime is <code>{{ averagePositionOpenDays }}</code> days
            </b-container>
        </rounded-container>

        <rounded-container variant="dark">
            <template v-slot:title>
                Locked: <strong>${{ formatHoldings(userStats.stocks.value_open_positions + userStats.crypto.value_open_positions) }}</strong>.
                YTD closed: <strong>${{ formatHoldings(userStats.stocks.ytd_closed_value + userStats.crypto.ytd_closed_value) }}</strong>.
            </template>

            <b-container class="stats-container p-3">
                <b-row>
                    <b-col cols=4>
                        Value locked
                    </b-col>
                    <b-col>
                        <span class="skin_in_the_game">
                            ${{ userStats.stocks.value_open_positions }} (S)
                        </span>
                    </b-col>
                    <b-col>
                        <span class="skin_in_the_game">
                            ${{ userStats.crypto.value_open_positions }} (C)    
                        </span>
                    </b-col>
                </b-row>

                <b-row class="pt-3">
                    <b-col cols=4>
                        Positions
                    </b-col>
                    <b-col>
                        {{ userStats.stocks.no_open_positions }} /
                        {{
                            userStats.stocks.no_open_positions +
                                userStats.stocks.no_closed_positions
                        }}
                        <small>
                        ({{
                            Math.round(
                                (userStats.stocks.no_open_positions /
                                    (userStats.stocks.no_closed_positions +
                                        userStats.stocks.no_open_positions)) *
                                    100
                            )
                        }}%)
                        </small>
                    </b-col>
                    <b-col>
                        {{ userStats.crypto.no_open_positions }} /
                        {{
                            userStats.crypto.no_open_positions +
                                userStats.crypto.no_closed_positions
                        }}
                        <small>
                        ({{
                            Math.round(
                                (userStats.crypto.no_open_positions /
                                    (userStats.crypto.no_closed_positions +
                                        userStats.crypto.no_open_positions)) *
                                    100
                            )
                        }}%)
                        </small>
                    </b-col>
                </b-row>

                <b-row class="pt-3">
                    <b-col cols=4>
                        YTD closed
                    </b-col>
                    <b-col>
                        ${{ userStats.stocks.ytd_closed_value }}<br />
                        {{ userStats.stocks.ytd_closed_positions }} pos.
                    </b-col>
                    <b-col>
                        ${{ userStats.crypto.ytd_closed_value }}<br />
                        {{ userStats.crypto.ytd_closed_positions }} pos.
                    </b-col>
                </b-row>
                
                <b-row class="pt-5 text-center">
                    <b-col>
                        <b-button :to="{ name: 'stalePositions' }" pill variant="primary" class="mr-2 mb-2">Stale Positions</b-button>
                        <b-button :to="{ name: 'portfolio' }" pill variant="primary" class="mr-2 mb-2">Portfolio overview</b-button>
                    </b-col>
                </b-row>
                <b-row class="text-center">
                    <b-col>
                        <b-button :to="{ name: 'etoroTrades' }" pill variant="info" class="mr-2 mb-2">eToro</b-button>
                        <b-button :to="{ name: 'binance' }" pill variant="info" class="mb-2">Binance</b-button>
                    </b-col>
                </b-row>
            </b-container>
        </rounded-container>

        <rounded-container variant="dark" v-if="assetsToUpdate.length">
            <template v-slot:title>
                Assets that require a balance update
            </template>

            <b-container class="stats-container p-3">
                <b-button 
                    v-for="asset in assetsToUpdate" 
                    :key="asset.public_id"
                    :to="{ name: 'asset', params: { symbol: asset.symbol } }"
                    class="mr-2 mb-2"
                    variant="outline-light"
                >
                    {{ asset.symbol }}
                </b-button>
            </b-container>
        </rounded-container>

        <system-health :health="mercuryHealth" />

    </div>
</template>

<script>

import { mapState } from 'vuex';
import api from '../mixins/api';
import formatter from '../mixins/formatter';
import BuysClosedChart from '../components/stats/BuysClosedChart.vue';
import ChartSkeleton from '../components/charts/ChartSkeleton.vue';
import RoundedContainer from '../components/style/RoundedContainer.vue';
// Reads /api/mercury_health, which reports what each price socket is doing right now.
import SystemHealth from '../components/stats/SystemHealth.vue';
import { CHART_COLOR_BUY, CHART_COLOR_SELL } from '@/constants/ui';

export default {
  components: { BuysClosedChart, ChartSkeleton, RoundedContainer, SystemHealth },
    name: 'Stats',
    mixins: [api, formatter],

    data() {
        return {
            barChartData: {},
            gotStatsData: false,
            averagePositionOpenDays: 0,
            assetPublicIdsToUpdate: [],
            mercuryHealth: null
        }
    },
    async mounted() {
        this.gotStatsData = false;

        this.loadMercuryHealth();

        const response = await this.apiGet('/api/system_status');
        if (!response) return;

        this.averagePositionOpenDays = response.data.average_days_open;

        if (response.data.assets_to_update?.length) {
            this.assetPublicIdsToUpdate = response.data.assets_to_update;
        }

        // process data for chart
        let labels = [];
        let buyData = [];
        let closedData = [];

        const buys = response.data.no_buys_30d;
        for (const index in buys) {
            labels.push(index);
            buyData.push(buys[index]);
        }

        const sells = response.data.no_sells_30d;
        for (const index in sells) {
            closedData.push(sells[index]);
        }

        this.barChartData = {
            labels: labels.reverse(),
            datasets: [
                {
                    label: 'Buys',
                    backgroundColor: CHART_COLOR_BUY,
                    data: buyData.reverse(),
                },
                {
                    label: 'Sells',
                    backgroundColor: CHART_COLOR_SELL,
                    data: closedData.reverse(),
                }
            ]
        }

        this.gotStatsData = true;
    },
    methods: {
        // Its own request, so a slow or dead Mercury never holds up the stats.
        async loadMercuryHealth() {
            const response = await this.apiGet('/api/mercury_health');

            /* A failed request is the state this panel exists to report, so it must not be
               left reading "checking". */
            this.mercuryHealth = response ? response.data : { online: false, feeds: [] };
        }
    },
    computed: {
        ...mapState(['userStats']),
        assetsToUpdate() {
            const conviction100k = [];
            const convictionHigh = [];
            const convictionRegular = [];
            const convictionLow = [];
            const convictionSnoozed = [];
            const convictionOther = [];

            for (const eachPublicId of this.assetPublicIdsToUpdate) {
                const eachAsset = this.$store.getters.getAssetByPublicId(eachPublicId);

                if (eachAsset.conviction === '100k') {
                    conviction100k.push(eachAsset);
                } else if (eachAsset.conviction === 'high') {
                    convictionHigh.push(eachAsset);
                } else if (eachAsset.conviction === null) {
                    convictionRegular.push(eachAsset);
                } else if (eachAsset.conviction === 'low') {
                    convictionLow.push(eachAsset);
                } else if (eachAsset.conviction === 'snoozed') {
                    convictionSnoozed.push(eachAsset);
                } else {
                    convictionOther.push(eachAsset)
                }
            }
            return [
                ...conviction100k, 
                ...convictionHigh, 
                ...convictionRegular, 
                ...convictionLow, 
                ...convictionSnoozed
            ];
        }
    },
};
</script>

